import { ChangeDetectorRef, Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AclService } from './acl.service';

/**
 * The service that allows you to control the role of a some-element, table, list, etc.
 *
 * @export
 * @class AclHasFunctionDirective
 * @implements {OnDestroy}
 *
 * @howToUse
 * ```ts
 * <some-element [aclHasFunction]="['FUNC__FUNC_APPROVAL_APPROVER__APPROVALS']"></some-element>
 * <some-element *aclHasFunction="['FUNC__FUNC_APPROVAL_APPROVER__APPROVALS']"></some-element>
 * ```
 */
@Directive({
  selector: '[aclHasFunction]',
  exportAs: 'acl',
})
export class AclHasFunctionDirective implements OnDestroy {
  private roles: string[];
  private readonly destroy$ = new Subject<void>();

  constructor(private _changeDetectorRef: ChangeDetectorRef, private aclService: AclService, private templateRef: TemplateRef<any>, private viewContainerRef: ViewContainerRef) {}


  @Input('aclHasFunction')
  set acl(value: string | string[] | null) {
    if (!value) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      return;
    }

    this.roles = typeof value === 'string' ? [value] : value;
    if (this.roles.length === 0) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      return;
    }

    this.updateView();
    // Get notified each time getAccountAbilityState state changes.
    this.aclService.getAccountAbilityState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(ability => this.updateView());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    const hasAnyPermission = this.aclService.hasAnyFunction(this.roles);
    this.viewContainerRef.clear();
    if (hasAnyPermission) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    }
    this._changeDetectorRef.markForCheck();
  }
}
